import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { useEffect } from "react";
import cx from "../../../lib/cx.js";
import Avatar from "../Avatar/Avatar";
import Icon from "../Icon/Icon";
import { getDisplayTag } from "../../../lib/display-utils";
import { isMobileDevice } from "../../../lib/device-utils.js";
import TruncatedName from "../TruncatedName/TruncatedName.jsx";
import { isSafariBrowser } from "../../../lib/is-safari.js";
import styles from "./userDisplay.css";

/**
 * Enum for item types.
 * Defines the type of item for a drop context.
 *
 * @property {string} USER - Represents a user item type, specifically a student.
 */
export const ItemType = {
  USER: "student",
};

/**
 * Displays the user's information including an avatar, name, and optional tag.
 * Optionally includes a draggable icon and a menu for additional items.
 * Not to be confused with the UserDisplay that lives in the ui folder!
 *
 * @param {Object} props An object containing properties to configure the UserDisplay component.
 * @param {JSX.Element} props.menuItems An array of menu items to display alongside the user information.
 * @param {Object} props.user An object representing the user's information.
 * @param {string} props.user.id The unique identifier for the user.
 * @param {string} props.user.name The name of the user.
 * @param {string} [props.user.tag] An optional tag to display next to the user's name.
 * @param {string} props.groupId The identifier for the group to which the user belongs.
 * @param {boolean} [props.draggable=false] Determines if the user display is draggable.
 * @param {boolean} [props.highlight=false] Determines if the user's avatar should be highlighted.
 * @param {boolean} [props.brighterBackground=false] Determines if the user's background should be brighter, for example used if the user is in a group.
 * @return {JSX.Element}
 * @constructor
 */
export const UserDisplay = ({
  menuItems,
  user,
  groupId,
  draggable = false,
  highlight = false,
  brighterBackground = false,
}) => {
  const isSafariOrMobile = isMobileDevice() || isSafariBrowser();

  const [{ isDragging }, ref, preview] = useDrag(
    () => ({
      type: ItemType.USER,
      item: {
        id: user.id,
        userNameAndTag: `${user.name}#${user.tag}`,
        groupId,
      },
      canDrag: draggable,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [draggable],
  );

  // Hide the native browser drag preview to avoid Safari/iOS issues and use our custom drag layer instead
  useEffect(() => {
    // only necessary on mobile devices or Safari browser to avoid ghost image
    if (isSafariOrMobile) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [isSafariOrMobile, preview]);

  const handleMouseDown = (e) => {
    // Prevent native text selection/dragging in Safari at drag start
    if (isSafariBrowser() && draggable) {
      e.preventDefault();
    }
  };

  const handleTouchStart = (e) => {
    if (isSafariBrowser() && draggable) {
      // Prevent iOS Safari from creating native drag images / selection
      e.preventDefault();
    }
  };

  return (
    <div
      className={cx(
        draggable
          ? styles.draggableUserDisplay({
              brighterBackground: brighterBackground,
            })
          : "",
        isDragging && !isMobileDevice()
          ? cx(styles.userDisplay, styles.isDraggingAndHTML5Backend)
          : styles.userDisplay,
      )}
      ref={ref}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className={styles.container}>
        {draggable && (
          <Icon
            name="dragger"
            className={styles.dragger({
              brighterBackground: brighterBackground,
            })}
          />
        )}

        <div className={styles.user}>
          <Avatar user={user} highlight={highlight} size="40px" />

          <p className={styles.userIdentifier}>
            <TruncatedName name={user.name} />

            {user.tag && (
              <span className={styles.tag}>({getDisplayTag(user.tag)})</span>
            )}
          </p>
        </div>
      </div>

      {menuItems && <div className={styles.menu}>{menuItems}</div>}
    </div>
  );
};
